import type { PanelPlugin, PluginContext } from '../core/plugin'
import type { MessageBridge } from './message-bridge'
import type { HistoryManager } from './history'
import { CONFIG } from './config'
import { ICONS } from './icons'
import { log } from './utils'

const FIT_OPTIONS: Array<{ value: string, label: string }> = [
  { value: 'fill', label: '填充' },
  { value: 'contain', label: '适应' },
  { value: 'cover', label: '平铺' },
  { value: 'scale-down', label: '缩小' },
]

const FIT_LABEL_MAP: Record<string, string> = FIT_OPTIONS.reduce((map, opt) => {
  map[opt.value] = opt.label
  return map
}, {} as Record<string, string>)

interface PendingImageUpload {
  requestId: string,
  element: HTMLImageElement,
  elementId: string,
  oldSrc: string,
  oldObjectFit: string,
  promise: Promise<void>,
  resolve: () => void,
  reject: (error: Error) => void,
}

export class ImagePanel implements PanelPlugin {
  readonly name = 'image-panel'

  private bridge: MessageBridge

  private history: HistoryManager

  private panel: HTMLDivElement | null = null

  private activeElement: HTMLElement | null = null

  private isVisible = false

  private _fileInput: HTMLInputElement | null = null

  private _previewUrl: string | null = null

  private _scrollHandler: (() => void) | null = null

  private _outsideHandler: ((e: MouseEvent) => void) | null = null

  private _initialSnapshot: { src: string, objectFit: string } | null = null

  // AI 生图"等待中的目标 elementId"。发请求时记录；切换/隐藏面板时清空。
  // 结果到达时若为 null 或与当前不匹配，则丢弃，避免错位替换。
  private _pendingAiTargetElementId: string | null = null

  private _pendingUploads = new Map<string, PendingImageUpload>()

  private _uploadSeq = 0

  constructor(messageBridge: MessageBridge, historyManager: HistoryManager) {
    this.bridge = messageBridge
    this.history = historyManager
  }

  get visible(): boolean {
    return this.isVisible
  }

  create(_ctx: PluginContext): void {
    this._createPanel()
  }

  private _createPanel(): void {
    const panel = document.createElement('div')
    panel.className = 'musk-image-panel'
    panel.setAttribute(CONFIG.TOOLBAR_ATTR, 'true')

    const toolbarCard = document.createElement('div')
    toolbarCard.className = 'mip-card mip-toolbar-card'

    const fitOptionsHtml = FIT_OPTIONS.map((opt) => (
      `<div class="mip-fit-option" data-fit="${opt.value}"><span class="mst-option-check">${ICONS.CHECK}</span><span class="mst-option-label">${opt.label}</span></div>`
    )).join('')

    toolbarCard.innerHTML = [
      '<div class="mip-buttons-row">',
      '  <div class="mip-fit-wrap">',
      '    <button class="mip-btn mip-fit-btn" data-tooltip="适配方式" type="button">',
      `      ${ICONS.IMAGE_FRAME}`,
      `      <span class="mip-fit-label">${FIT_OPTIONS[0].label}</span>`,
      `      ${ICONS.CHEVRON_DOWN}`,
      '    </button>',
      `    <div class="mip-fit-dropdown">${fitOptionsHtml}</div>`,
      '  </div>',
      '  <div class="mip-divider"></div>',
      '  <button class="mip-btn mip-upload-btn" data-tooltip="本地文件替换" type="button">',
      '    <span class="mip-upload-icon-slot">',
      `      <span class="mip-upload-icon">${ICONS.UPLOAD_REPLACE}</span>`,
      `      <span class="mip-upload-spinner">${ICONS.LOADING_SPINNER}</span>`,
      '    </span>',
      '    <span class="mip-upload-label">替换</span>',
      '  </button>',
      '  <div class="mip-divider"></div>',
      '  <div class="mip-ai-section">',
      `    <span class="musk-ai-label">${ICONS.AI_SPARKLE}</span>`,
      '    <input type="text" class="musk-ai-input" placeholder="描述你想要的图片效果...">',
      `    <button class="musk-ai-send" type="button" disabled>${ICONS.SEND_ARROW}</button>`,
      '  </div>',
      '</div>',
    ].join('\n')

    panel.appendChild(toolbarCard)

    const resultCard = document.createElement('div')
    resultCard.className = 'mip-card mip-result-card'
    resultCard.style.display = 'none'
    resultCard.innerHTML = [
      '<div class="mip-image-preview"><img src="" alt=""></div>',
      '<div class="mip-image-actions">',
      `  <button class="mip-action-btn mip-image-regenerate" type="button">${ICONS.RETRY}<span>重试</span></button>`,
      `  <button class="mip-action-btn mip-image-apply" type="button">${ICONS.REPLACE}<span>替换</span></button>`,
      `  <button class="mip-action-btn mip-image-discard" type="button">${ICONS.TRASH}<span>弃用</span></button>`,
      '</div>',
    ].join('\n')
    panel.appendChild(resultCard)

    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = CONFIG.IMAGE_UPLOAD.ACCEPT_ATTR
    fileInput.style.display = 'none'
    fileInput.setAttribute(CONFIG.TOOLBAR_ATTR, 'true')
    document.body.appendChild(fileInput)
    this._fileInput = fileInput

    document.body.appendChild(panel)
    this.panel = panel
    this._bindEvents()
    this._setupScrollHandler()
  }

  private _bindEvents(): void {
    const aiInput = this.panel!.querySelector<HTMLInputElement>('.musk-ai-input')
    const aiSend = this.panel!.querySelector<HTMLButtonElement>('.musk-ai-send')
    const fitWrap = this.panel!.querySelector<HTMLElement>('.mip-fit-wrap')!

    const clearAiInput = (): void => {
      if (aiInput) { aiInput.value = ''; aiInput.disabled = false }
      if (aiSend) { aiSend.disabled = true; aiSend.classList.remove('musk-ai-loading') }
    }

    const doAiSend = (): void => {
      if (!aiInput) return
      const text = aiInput.value.trim()
      if (!text || !this.activeElement) return
      const elementId = this.activeElement.getAttribute('data-musk-element-id') || ''
      this._hidePreview()
      if (aiSend) {
        aiSend.disabled = true
        aiSend.classList.add('musk-ai-loading')
      }
      aiInput.disabled = true
      this._setUploadEnabled(false)
      this._pendingAiTargetElementId = elementId
      this.bridge.send('AI_GENERATE_IMAGE', {
        elementId,
        prompt: text,
        referenceImageUrl: (this.activeElement as HTMLImageElement).src || '',
      })
      log('AI 生图请求已发送', { mode: 'image', elementId })
    }

    this.panel!.addEventListener('mousedown', (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (t.closest('.musk-ai-input')) { e.stopPropagation(); return }
      e.stopPropagation()
      if (!t.closest('.mip-upload-btn')) e.preventDefault()
    }, true)

    this._outsideHandler = (e: MouseEvent) => {
      if (!this.isVisible) return
      const t = e.target as HTMLElement
      if (!fitWrap.contains(t)) {
        this._closeFitDropdown()
      }
    }
    document.addEventListener('mousedown', this._outsideHandler, true)

    this.panel!.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation()
      const t = e.target as HTMLElement

      if (t.closest('.mip-fit-btn')) {
        e.preventDefault()
        if (fitWrap.classList.contains('mip-open')) {
          this._closeFitDropdown()
        } else {
          this._openFitDropdown()
        }
        return
      }
      const fitOpt = t.closest('.mip-fit-option') as HTMLElement | null
      if (fitOpt) {
        e.preventDefault()
        const val = fitOpt.getAttribute('data-fit')
        this._applyObjectFit(val)
        this._closeFitDropdown()
        return
      }
      if (t.closest('.mip-upload-btn')) {
        e.preventDefault()
        if (this._isAIBusy()) return
        if (this._hasPendingUploadForActiveElement()) return
        this._fileInput!.click()
        return
      }
      if (t.closest('.musk-ai-send')) {
        e.preventDefault(); doAiSend(); return
      }
      if (t.closest('.mip-image-discard')) {
        e.preventDefault(); this._hidePreview(); clearAiInput(); return
      }
      if (t.closest('.mip-image-regenerate')) {
        e.preventDefault(); this._hidePreview(); doAiSend(); return
      }
      if (t.closest('.mip-image-apply')) {
        e.preventDefault(); this._applyPreview(); clearAiInput()
      }
    }, true)

    this._fileInput!.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement
      const file = target.files?.[0]
      if (!file) return
      this._fileInput!.value = ''
      this._uploadFile(file)
    })

    if (aiInput) {
      aiInput.addEventListener('keydown', (e: KeyboardEvent) => {
        e.stopPropagation()
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doAiSend() }
        if (e.key === 'Escape') { this.hide() }
      })
      aiInput.addEventListener('input', () => {
        if (aiSend) aiSend.disabled = !aiInput.value.trim()
      })
    }
  }

  private _setupScrollHandler(): void {
    this._scrollHandler = () => {
      if (this.isVisible && this.activeElement) this._updatePosition()
    }
    window.addEventListener('scroll', this._scrollHandler, { passive: true })
  }

  private _isAIBusy(): boolean {
    const sendBtn = this.panel?.querySelector('.musk-ai-send')
    return !!sendBtn && sendBtn.classList.contains('musk-ai-loading')
  }

  private _setUploadEnabled(enabled: boolean): void {
    const btn = this.panel?.querySelector<HTMLButtonElement>('.mip-upload-btn')
    if (!btn) return
    if (enabled) {
      btn.classList.remove('mip-upload-disabled')
      btn.removeAttribute('disabled')
    } else {
      btn.classList.add('mip-upload-disabled')
      btn.setAttribute('disabled', 'true')
    }
  }

  private _createUploadRequestId(): string {
    this._uploadSeq += 1
    return `musk-image-upload-${Date.now()}-${this._uploadSeq}`
  }

  private _hasPendingUploadForActiveElement(): boolean {
    if (!this.activeElement) return false
    return Array.from(this._pendingUploads.values()).some((upload) => upload.element === this.activeElement)
  }

  private _syncUploadButtonState(): void {
    const btn = this.panel?.querySelector<HTMLButtonElement>('.mip-upload-btn')
    if (!btn) return
    if (this._hasPendingUploadForActiveElement()) {
      btn.classList.add('mip-uploading')
    } else {
      btn.classList.remove('mip-uploading')
    }
  }

  hasPendingUploads(): boolean {
    return this._pendingUploads.size > 0
  }

  async waitForPendingUploads(): Promise<void> {
    const pendingUploads = Array.from(this._pendingUploads.values())
    if (pendingUploads.length === 0) return

    this.bridge.send('SHOW_TOAST', { type: 'info', message: '图片上传中，请稍候' })
    await Promise.all(pendingUploads.map((upload) => upload.promise))
  }

  private _validateUploadFile(file: File): string | null {
    const { ACCEPT_EXTS, MAX_SIZE_MB } = CONFIG.IMAGE_UPLOAD
    const lowerName = (file.name || '').toLowerCase()
    const matchedExt = ACCEPT_EXTS.some((ext) => lowerName.endsWith(ext))
    if (!matchedExt) {
      return `不支持的图片格式，请上传 ${ACCEPT_EXTS.join('、')} 格式`
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `图片大小不能超过 ${MAX_SIZE_MB}MB`
    }
    return null
  }

  private _uploadFile(file: File): void {
    if (!this.activeElement || this.activeElement.tagName !== 'IMG') return

    const reason = this._validateUploadFile(file)
    if (reason) {
      this.bridge.send('SHOW_TOAST', { type: 'error', message: reason })
      log('图片上传被本地校验拦截', reason)
      return
    }

    const btn = this.panel!.querySelector<HTMLButtonElement>('.mip-upload-btn')!
    btn.classList.add('mip-uploading')

    const element = this.activeElement as HTMLImageElement
    const elementId = element.getAttribute('data-musk-element-id') || ''
    const requestId = this._createUploadRequestId()
    let resolveUpload: () => void = () => {}
    let rejectUpload: (error: Error) => void = () => {}
    const promise = new Promise<void>((resolve, reject) => {
      resolveUpload = resolve
      rejectUpload = reject
    })
    promise.catch(() => undefined)

    this._pendingUploads.set(requestId, {
      requestId,
      element,
      elementId,
      oldSrc: element.src,
      oldObjectFit: element.style.objectFit || '',
      promise,
      resolve: resolveUpload,
      reject: rejectUpload,
    })

    this.bridge.send('IMAGE_UPLOAD_REQUEST', {
      requestId,
      file,
      fileName: file.name,
      fileType: file.type,
      elementId,
    })
    log('图片上传请求已发送', { requestId, elementId, fileName: file.name })
  }

  private _commitImageReplacement(
    element: HTMLImageElement,
    elementId: string,
    oldSrc: string,
    oldObjectFit: string,
    newSrc: string,
  ): void {
    const currentFit = element.style.objectFit || ''
    const styleChanges: Array<{ property: string, oldValue: string, newValue: string }> = []
    if (currentFit !== oldObjectFit) {
      styleChanges.push({ property: 'objectFit', oldValue: oldObjectFit, newValue: currentFit })
    }
    this.history.addOperation({
      type: 'image-compound',
      elementId,
      oldSrc,
      newSrc,
      styleChanges,
    })
    this.bridge.send('EDIT_END', {
      elementId,
      changed: true,
      imageChange: true,
      oldSrc,
      newSrc,
      imageStyleChanges: styleChanges,
    })
    this.bridge.send('HAS_CHANGES_UPDATED', { hasChanges: true })
  }

  private _findPendingUpload(payload: Record<string, unknown>): PendingImageUpload | null {
    const requestId = typeof payload.requestId === 'string' ? payload.requestId : ''
    if (requestId) {
      return this._pendingUploads.get(requestId) || null
    }

    const elementId = typeof payload.elementId === 'string' ? payload.elementId : ''
    if (!elementId) return null
    return Array.from(this._pendingUploads.values()).find((upload) => upload.elementId === elementId) || null
  }

  handleUploadResult(payload: Record<string, unknown>): void {
    const pendingUpload = this._findPendingUpload(payload || {})
    const requestId = pendingUpload?.requestId || ''
    if (requestId) {
      this._pendingUploads.delete(requestId)
    }
    this._syncUploadButtonState()

    if (!payload || !payload.url || typeof payload.url !== 'string') {
      const message = typeof payload?.error === 'string' ? payload.error : '图片上传失败，请重试'
      if (pendingUpload) pendingUpload.reject(new Error(message))
      this.bridge.send('SHOW_TOAST', { type: 'error', message })
      log('上传失败，无返回URL', { requestId, message })
      return
    }

    if (!pendingUpload) {
      log('收到无法匹配目标的图片上传结果，已忽略', payload)
      return
    }

    if (!pendingUpload.element.isConnected) {
      const message = '图片已离开当前页面，请重新上传'
      pendingUpload.reject(new Error(message))
      this.bridge.send('SHOW_TOAST', { type: 'error', message })
      log('上传成功但目标图片已不存在', { requestId })
      return
    }

    this._hidePreview()
    pendingUpload.element.src = payload.url
    if (this.activeElement === pendingUpload.element && this._initialSnapshot) {
      this.bridge.send('HAS_CHANGES_UPDATED', { hasChanges: true })
    } else {
      this._commitImageReplacement(
        pendingUpload.element,
        pendingUpload.elementId,
        pendingUpload.oldSrc,
        pendingUpload.oldObjectFit,
        payload.url,
      )
    }
    pendingUpload.resolve()
    log('图片已替换', { requestId, new: payload.url })
  }

  handleAIImageResult(payload: Record<string, unknown>): void {
    const expected = this._pendingAiTargetElementId
    this._pendingAiTargetElementId = null
    const currentElementId = this.activeElement?.getAttribute('data-musk-element-id') || null

    const aiInput = this.panel?.querySelector<HTMLInputElement>('.musk-ai-input')
    const aiSend = this.panel?.querySelector<HTMLButtonElement>('.musk-ai-send')
    if (aiSend) aiSend.classList.remove('musk-ai-loading')
    // 保留提示词，便于用户基于同一提示词「重试」
    if (aiInput) aiInput.disabled = false
    if (aiSend) aiSend.disabled = !aiInput?.value.trim()

    this._setUploadEnabled(true)

    if (!expected) {
      log('AI 生图结果已过期（面板已切换/关闭），丢弃')
      return
    }
    const payloadElementId = payload && (payload.elementId as string | undefined)
    if (payloadElementId && payloadElementId !== expected) {
      log('AI 生图结果 elementId 与请求不一致，丢弃', {
        expected,
        got: payloadElementId,
      })
      return
    }
    if (currentElementId !== expected) {
      log('AI 生图结果与当前 activeElement 不一致，丢弃', {
        expected,
        current: currentElementId,
      })
      return
    }
    if (!payload || !payload.url) return
    this._previewUrl = payload.url as string
    const resultCard = this.panel!.querySelector<HTMLElement>('.mip-result-card')!
    const img = resultCard.querySelector<HTMLImageElement>('img')!
    img.src = payload.url as string
    resultCard.style.display = 'block'
    img.onload = () => {
      this._updatePosition()
    }
  }

  private _hidePreview(): void {
    if (!this.panel) return
    const resultCard = this.panel.querySelector<HTMLElement>('.mip-result-card')
    if (!resultCard) return
    const img = resultCard.querySelector<HTMLImageElement>('img')
    if (img) {
      img.onload = null
      img.src = ''
    }
    resultCard.style.display = 'none'
    this._previewUrl = null
    this._updatePosition()
  }

  private _applyPreview(): void {
    if (!this._previewUrl || !this.activeElement || this.activeElement.tagName !== 'IMG') return;
    (this.activeElement as HTMLImageElement).src = this._previewUrl
    this.bridge.send('HAS_CHANGES_UPDATED', { hasChanges: true })
    log('图片已替换(AI)', { new: this._previewUrl })
    this._hidePreview()
  }

  show(element: HTMLElement, _ctx: PluginContext): void {
    if (!this.panel) this._createPanel()
    const newElementId = element.getAttribute('data-musk-element-id') || ''
    if (this._pendingAiTargetElementId && this._pendingAiTargetElementId !== newElementId) {
      log('ImagePanel 切换到其他元素，作废 pending AI 请求', {
        was: this._pendingAiTargetElementId,
        now: newElementId,
      })
      this._pendingAiTargetElementId = null
    }
    if (this.activeElement && this.activeElement !== element) {
      this._commitChanges()
      this.activeElement.classList.remove('musk-general-editing')
    }
    this.activeElement = element
    this._initialSnapshot = {
      src: (element as HTMLImageElement).src,
      objectFit: element.style.objectFit || '',
    }
    element.classList.add('musk-general-editing')
    element.classList.remove('musk-general-hover')
    this.panel!.style.display = 'block'
    this.isVisible = true
    this._hidePreview()
    this._syncUploadButtonState()
    this._setUploadEnabled(true)
    this._syncFitState()
    this._updatePosition()
    const input = this.panel!.querySelector<HTMLInputElement>('.musk-ai-input')
    const sendBtn = this.panel!.querySelector<HTMLButtonElement>('.musk-ai-send')
    if (input) input.value = ''
    if (sendBtn) sendBtn.disabled = true
    this._closeFitDropdown()
  }

  private _openFitDropdown(): void {
    const fitWrap = this.panel?.querySelector<HTMLElement>('.mip-fit-wrap')
    const dd = fitWrap?.querySelector<HTMLElement>('.mip-fit-dropdown')
    if (!fitWrap || !dd) return
    dd.classList.remove('mip-drop-up')
    fitWrap.classList.add('mip-open')
    this._adjustFitPlacement(dd)
  }

  private _closeFitDropdown(): void {
    const fitWrap = this.panel?.querySelector<HTMLElement>('.mip-fit-wrap')
    if (fitWrap) fitWrap.classList.remove('mip-open')
    const dd = fitWrap?.querySelector<HTMLElement>('.mip-fit-dropdown')
    if (dd) dd.classList.remove('mip-drop-up')
  }

  // 与 toolbar 的 _adjustDropdownPlacement 等价：根据 viewport 决定朝上/朝下
  // fit dropdown 内容固定（4 项），不需要 max-height，只切换朝向即可
  private _adjustFitPlacement(dd: HTMLElement): void {
    const wrap = dd.parentElement
    if (!wrap) return
    const wrapRect = wrap.getBoundingClientRect()
    const viewportH = window.innerHeight
    const margin = 10
    const safety = 8
    const spaceBelow = Math.max(0, viewportH - wrapRect.bottom - margin - safety)
    const spaceAbove = Math.max(0, wrapRect.top - margin - safety)
    const desiredH = dd.scrollHeight
    if (spaceBelow < desiredH && spaceAbove > spaceBelow) {
      dd.classList.add('mip-drop-up')
    } else {
      dd.classList.remove('mip-drop-up')
    }
  }

  private _commitChanges(): void {
    if (!this.activeElement || !this._initialSnapshot) return
    const el = this.activeElement as HTMLImageElement
    const elementId = el.getAttribute('data-musk-element-id') || ''
    const currentSrc = el.src
    const currentFit = el.style.objectFit || ''
    const srcChanged = currentSrc !== this._initialSnapshot.src
    const styleChanges: Array<{ property: string, oldValue: string, newValue: string }> = []
    if (currentFit !== this._initialSnapshot.objectFit) {
      styleChanges.push({ property: 'objectFit', oldValue: this._initialSnapshot.objectFit, newValue: currentFit })
    }
    if (!srcChanged && styleChanges.length === 0) {
      this._initialSnapshot = null
      return
    }
    this.history.addOperation({
      type: 'image-compound',
      elementId,
      oldSrc: this._initialSnapshot.src,
      newSrc: currentSrc,
      styleChanges,
    })
    this.bridge.send('EDIT_END', {
      elementId,
      changed: true,
      imageChange: true,
      oldSrc: this._initialSnapshot.src,
      newSrc: currentSrc,
      imageStyleChanges: styleChanges,
    })
    this.bridge.send('HAS_CHANGES_UPDATED', { hasChanges: true })
    this._initialSnapshot = null
    log('图片复合操作已提交', { elementId, srcChanged, styleChanges: styleChanges.length })
  }

  hide(commit = true): void {
    if (!this.panel) return
    if (commit) {
      this._commitChanges()
    } else {
      // silent 模式：丢弃当前编辑快照，不提交到 history
      this._initialSnapshot = null
    }
    this.panel.style.display = 'none'
    this.isVisible = false
    this._hidePreview()
    this._syncUploadButtonState()
    this._setUploadEnabled(true)
    this._pendingAiTargetElementId = null
    const aiInput = this.panel.querySelector<HTMLInputElement>('.musk-ai-input')
    const aiSend = this.panel.querySelector<HTMLButtonElement>('.musk-ai-send')
    if (aiSend) { aiSend.disabled = true; aiSend.classList.remove('musk-ai-loading') }
    if (aiInput) { aiInput.disabled = false; aiInput.value = '' }
    this._closeFitDropdown()
    if (this.activeElement) {
      this.activeElement.classList.remove('musk-general-editing')
      this.activeElement = null
    }
  }

  destroy(): void {
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler)
      this._scrollHandler = null
    }
    if (this._outsideHandler) {
      document.removeEventListener('mousedown', this._outsideHandler, true)
      this._outsideHandler = null
    }
    this._commitChanges()
    if (this.panel) { this.panel.remove(); this.panel = null }
    if (this._fileInput) { this._fileInput.remove(); this._fileInput = null }
    this._pendingUploads.forEach((upload) => {
      upload.reject(new Error('编辑器已销毁，图片上传已取消'))
    })
    this._pendingUploads.clear()
    this.activeElement = null
    this.isVisible = false
  }

  private _syncFitState(): void {
    if (!this.panel || !this.activeElement) return
    const current = window.getComputedStyle(this.activeElement).objectFit || 'fill'
    const opts = this.panel.querySelectorAll<HTMLElement>('.mip-fit-option')
    for (let i = 0; i < opts.length; i++) {
      const val = opts[i].getAttribute('data-fit')
      if (val === current) opts[i].classList.add('mip-selected')
      else opts[i].classList.remove('mip-selected')
    }
    const label = this.panel.querySelector<HTMLElement>('.mip-fit-label')
    if (label) label.textContent = FIT_LABEL_MAP[current] || FIT_LABEL_MAP.fill
  }

  private _applyObjectFit(fitValue: string | null): void {
    if (!this.activeElement || !fitValue) return
    if ((this.activeElement.style.objectFit || '') === fitValue) {
      this._syncFitState()
      return
    }
    this.activeElement.style.objectFit = fitValue
    this.bridge.send('HAS_CHANGES_UPDATED', { hasChanges: true })
    this._syncFitState()
    log('图片适配方式已更新', { objectFit: fitValue })
  }

  private _updatePosition(): void {
    if (!this.activeElement || !this.panel) return
    const rect = this.activeElement.getBoundingClientRect()
    const panelH = this.panel.offsetHeight
    const panelW = this.panel.offsetWidth
    const gap = 8
    let top = rect.bottom + gap
    if (top + panelH > window.innerHeight - 10) top = rect.top - panelH - gap
    if (top < 10) top = 10
    let left = rect.left + (rect.width - panelW) / 2
    if (left + panelW > window.innerWidth - 10) left = window.innerWidth - panelW - 10
    if (left < 10) left = 10
    this.panel.style.top = `${top}px`
    this.panel.style.left = `${left}px`
  }
}
